import { Button, HStack, Input, InputGroup } from '@chakra-ui/react';
import { useState } from 'react';
import { FaCheck, FaTimes } from 'react-icons/fa';
import { LuPencil } from 'react-icons/lu';
import { toast } from 'react-toastify';
import { updateContractServicePrice } from 'services/contract';
import { usePermissions } from 'shared/hooks/usePermissions';
import { Product } from 'shared/models/product';
import { useFinancialContext } from '../../FinancialContext';

interface EditServicePriceButtonProps {
  loading?: boolean;
  service: Product;
}

const EditServicePriceButton = ({
  loading,
  service,
}: EditServicePriceButtonProps) => {
  const { displayedContract: selectedContract, reloadContracts } =
    useFinancialContext();
  const { e } = usePermissions();

  const [isEditing, setIsEditing] = useState(false);
  const [priceInput, setPriceInput] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  if (!e('contract-service-price-update')) return null;

  const setPriceState = (value: number | string) => {
    const v =
      typeof value === 'string'
        ? Number(String(value).replace(/\D/g, '')) / 100
        : value;
    setPriceInput(v.toFixed(2).replace('.', ','));
  };

  const handleEditClick = () => {
    setPriceState(service.value ?? 0);
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setPriceInput('');
  };

  const handleSave = async () => {
    const newPrice = parseFloat(priceInput.replace(',', '.'));
    if (isNaN(newPrice) || newPrice < 0) {
      toast.error('Valor inválido');
      return;
    }
    if (!service.id) return;

    setIsSaving(true);
    try {
      await updateContractServicePrice(service.id, {
        price: newPrice,
      });
      toast.success('Valor atualizado com sucesso');
      setIsEditing(false);
      reloadContracts(false, selectedContract ?? undefined);
    } catch {
      toast.error('Não foi possível atualizar o valor');
    } finally {
      setIsSaving(false);
    }
  };

  if (isEditing) {
    return (
      <HStack spacing={1}>
        <InputGroup size="sm">
          <Input
            w="100px"
            value={priceInput}
            onChange={(e) => setPriceState(e.target.value)}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') void handleSave();
              if (e.key === 'Escape') handleCancel();
            }}
          />
        </InputGroup>
        <Button
          size="xs"
          variant="plainIcon"
          color="yGreen.500"
          isLoading={isSaving}
          onClick={() => void handleSave()}
        >
          <FaCheck size={11} />
        </Button>
        <Button
          size="xs"
          variant="plainIcon"
          color="yRed.500"
          onClick={handleCancel}
          isDisabled={isSaving}
        >
          <FaTimes size={11} />
        </Button>
      </HStack>
    );
  }

  return (
    <Button
      isDisabled={loading}
      size="xs"
      variant="plainIcon"
      color="yPrimary.600"
      onClick={handleEditClick}
    >
      <LuPencil size={13} />
    </Button>
  );
};

export default EditServicePriceButton;
